import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { openFacets } from '../stores/productSearchSlice';
import { selectFacetsOpen, selectOptions, selectSearchResultCount } from '../stores/selectors';
import { ConnectedSearchResultsList } from '../Connectors/ConnectedSearchResultsList';
import FilterBadges from '../Connectors/ConnectedFilterBadges';
import SortOrder from '../Connectors/ConnectedSortOrder';
import ConnectedPagination from '../Connectors/ConnectedPagination';
import { ConnectedCategoryFacets } from '../Connectors/ConnectedCategoryFacets';
import styles from './SearchModal.module.scss';

export interface ISearch {
  showAddToCartButton?: boolean;
  title: string;
  onCloseSearch: () => void;
}

// eslint-disable-next-line max-len
export const SearchModal = ({ showAddToCartButton = true, title, onCloseSearch }: ISearch) => {
  const { t } = useTranslation('search');
  const dispatch = useDispatch();
  const isFacetsOpen = useSelector((state) => selectFacetsOpen(state));
  const searchResultCount = useSelector((state) => selectSearchResultCount(state)) ?? 0;
  const hasSearchResults = searchResultCount > 0;
  const { searchTerm = '' } = useSelector((state) => selectOptions(state));

  return (
    <div className={styles.ModalWrapper} data-testid="SearchModal">
      <div className={styles.SearchModal}>
        <div className="container">
          <div className="row">
            <div className={`${styles.Facets} ${!hasSearchResults ? styles.FacetsNoResults : ''} col-auto`}>
              <ConnectedCategoryFacets isOpen={isFacetsOpen} />
            </div>

            <div className={`${styles.Results} col pt-3 pl-3`}>
              <div className="row">
                <div className="col-8 col-md-auto mr-auto">
                  <span className="h1 d-inline-block">{title}</span>
                  <span className="h2 mb-0">{t('search:product-count', { count: searchResultCount })}</span>
                </div>
                <div className="col-auto ml-auto">
                  <button type="button" className="btn btn-link close" onClick={onCloseSearch} data-testid="SearchModal:closeSearchButton">
                    <span className="fa fa-times text-lg text-gray-900"></span>
                    <span className="sr-only">{t('search:button.facets-open')}</span>
                  </button>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <FilterBadges />
                </div>
              </div>
              <div className="row align-items-center mt-3">
                <div className="col-auto col-md-4">{hasSearchResults && <SortOrder />}</div>
                <div className="col-auto ml-auto d-lg-none">
                  <button
                    className={'btn btn-outline-primary btn-lg'}
                    onClick={() => dispatch(openFacets())}
                    type="button"
                    data-testid="openFacetsButton"
                  >
                    <span className="fa fa-regular fa-filter d-xsplus-none"></span>
                    <span className="d-none d-xsplus-inline">{t('search:button.facets-open')}</span>
                  </button>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  {hasSearchResults && (
                    <ConnectedSearchResultsList
                      showAddToCartButton={showAddToCartButton}
                      fallbackImage={{
                        src: '/-/jssmedia/Project/Tenants/CarelLurvink/shared/Logo/no-image.png',
                        alt: ''
                      }}
                    />
                  )}

                  {!hasSearchResults && (
                    <>
                      <h2 className="h2 mb-2">{t('search:title.no-results')}</h2>
                      <p className="text-muted">{t('search:message.no-results', { searchTerm })}</p>
                      <a href="/webshop" className="btn btn-theme btn-xl">
                        {t('search:button.all-products')}
                      </a>
                    </>
                  )}
                </div>
                <div className="col-12 pb-3">
                  <ConnectedPagination />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
